import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { GetPatentClms } from '@/infrastructure/api-functions/interface/get-patent-clms.interface';
import { GetMermaid } from '@/infrastructure/api-functions/interface/get-mermaid.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import { Symbols } from '@/constants';
import type VueI18n from 'vue-i18n';
import _ from 'lodash';
import { HistoryService } from '@/services/history/service/history.service';

export const AiClmsService = Symbol('AiClmsService');

export enum ClmsStep {
    CLMS_SEARCH = 'CLMS_SEARCH',
    CLMS_FLOW = 'CLMS_FLOW',
}

const EXECUTING = 'executing';
const UNEXECUTED  = 'unexecuted';
const SUCCESS = 'success';
const FAIL = 'fail';

type ProcessStatus = typeof EXECUTING | typeof UNEXECUTED | typeof SUCCESS | typeof FAIL;

interface MockProcess {
    patent: ProcessStatus;
    structure: ProcessStatus;
    main: ProcessStatus;
    flow: ProcessStatus;
}

interface ClmsData {
    pn?: string;
    title?: string;
    claim?: string;
}

export interface AiClmsService {
    step: ClmsStep;
    pn: string;
    loading: boolean;
    nodata: boolean;
    mockProcess: MockProcess;
    clmsData: ClmsData;
    chartData: string;
    chartLoading: boolean;

    getPatentInfo: () => Promise<void>;
    closeNodataTips: () => void;
    getFlowChartData: () => Promise<void>;
    restart: () => void;
}

@injectable()

export class AiClmsServiceImpl implements AiClmsService {
    public step: ClmsStep = ClmsStep.CLMS_SEARCH;
    public pn: string = '';
    public loading: boolean = false;
    public nodata: boolean = false;
    public mockProcess: MockProcess = {
        patent: EXECUTING,
        structure: UNEXECUTED,
        main: UNEXECUTED,
        flow: UNEXECUTED,
    };
    public clmsData: ClmsData = {};
    public chartData: string = '';
    public chartLoading: boolean = false;
    constructor(
        @inject(Message)
        private _message: Message,
        @inject(Symbols.VueI18n)
        private _i18n: VueI18n,
        @inject(GetPatentClms)
        private getPatentClmsApi: GetPatentClms,
        @inject(GetMermaid)
        private getMermaidApi: GetMermaid,
        @inject(HistoryService)
        private _historyService: HistoryService,
    ) {}

    private _resetMockProcess() {
        this.mockProcess = {
            patent: EXECUTING,
            structure: UNEXECUTED,
            main: UNEXECUTED,
            flow: UNEXECUTED,
        };
    }
    async getPatentInfo() {
        if (this.loading) return;
        this.loading = true;
        this._resetMockProcess();
        let clmsData:ClmsData = {};
        try {
            const taskId = this._historyService.getModelData().taskId;

            [clmsData ] = await Promise.all([
                this.getPatentClmsApi.getPatentClms({ task_id: taskId, query: 'PN:'+this.pn }),
                this._patentClmsProcess(),
            ]);
        } catch(err:any) {
            this._message.error({
                message: err._error_msg || this._i18n.t('commonStatusCode.400'),
            });
        }
        if (_.isEmpty(clmsData)) {
            this.nodata = true;
            this.loading = false;
            return;
        }

        this.clmsData = clmsData;
        this.mockProcess.flow = SUCCESS;
        this.loading = false;
        this.getFlowChartData();
        this.step = ClmsStep.CLMS_FLOW;
    }

    // 假的，为了增强AI体验特意做的延迟loading
    private async _patentClmsProcess() {
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                this.mockProcess.patent = SUCCESS;
                this.mockProcess.structure = EXECUTING;
                resolve();
            }, 1000);
            
        })
        .then(() => {
            return new Promise<void>((resolve) => {
                setTimeout(() => {
                    this.mockProcess.structure = SUCCESS;
                    this.mockProcess.main = EXECUTING;
                    resolve();
                }, 1000);     
            });
        })
        .then(() => {
            return new Promise<void>((resolve) => {
                setTimeout(() => {
                    this.mockProcess.main = SUCCESS;
                    this.mockProcess.flow = EXECUTING;
                    resolve();
                }, 1000);     
            });
        })
        .then(() => {
            return new Promise<void>((resolve) => {
                setTimeout(() => {
                    resolve();
                }, 1000);     
            });
        });
    }

    closeNodataTips() {
        this.nodata = false;
    }

    async getFlowChartData() {
        this.chartLoading = true;
        try {
            const taskId = this._historyService.getModelData().taskId;
            this.chartData = await this.getMermaidApi.getMermaid({ task_id: taskId, tech_desc: this.clmsData.claim || '', pn: this.clmsData.pn || '' });
        } catch(err:any) {

            this._message.error({
                message: err._error_msg,
            });
        }
        this.chartLoading = false;
    }

    restart() {
        this.step = ClmsStep.CLMS_SEARCH;
        this.pn = '';
        this.loading = false;
        this.nodata = false;
        this.mockProcess = {
            patent: EXECUTING,
            structure: UNEXECUTED,
            main: UNEXECUTED,
            flow: UNEXECUTED,
        };
        this.clmsData = {};
        this.chartData = '';
        this.chartLoading = false;
    }
}